
/**
 * LocalPresence — referans-parite (features/presence/state/LocalPresence.ts) portu.
 *
 * Kullanıcının KENDİ (yerel) presence'ını hesaplayan mobx singleton'ı. Idle
 * durumu, kullanıcının seçtiği status, mobil-yerleşim ve özel durum (custom
 * status) bilgilerini birleştirip sunucuya YAYINLANACAK etkin presence'ı üretir.
 * `presenceKey` değiştikçe (GatewayConnection/broadcast binder tarafından
 * gözlenerek) sunucuya presence gönderilir. TEBHOS'ta yalnızca "gelen presence"
 * tutuluyordu (PresenceStore); bu dosya "giden/kendi presence" katmanını ekler.
 *
 * TEBHOS uyarlamaları (kaynak-referanstan farklar):
 *   - `@tebhos/constants` StatusConstants YOK → TEBHOS `UserStatus` literal tipi +
 *     yerel `StatusTypes` sabitleri + yerel `normalizeStatus` kullanılır.
 *   - `AccountStorage.AccountPresenceIntent` altyapısı TEBHOS'ta YOK → intent tipi
 *     bu dosyada yapısal (structural) olarak tanımlanır. capture/restore mantığı
 *     birebir portlandı ama kalıcılaştırma (AccountStorage) henüz bağlı DEĞİL —
 *     intent'i çağıran/saklayan bir katman gelene dek atıl (restoreIntent(null)).
 *   - `MobileLayout` singleton'ı TEBHOS'ta `MobileLayoutStore` adıyla export edilir.
 *   - Ayar yüzeyi enjekte edilir (`setLocalPresenceUserSettings`) — kaynak-referans ile aynı;
 *     TEBHOS adaptörü LocalPresenceBroadcast.ts içindedir.
 */

import {deferUntilModulesLoaded} from '@app/features/platform/utils/DeferUntilModulesLoaded';
import type {UserStatus} from '@app/features/presence/state/PresenceStore';
import Idle from '@app/features/ui/state/Idle';
import MobileLayout from '@app/features/ui/state/MobileLayoutStore';
import type {CustomStatus, GatewayCustomStatusPayload} from '@app/features/user/state/CustomStatus';
import {customStatusToKey, normalizeCustomStatus, toGatewayCustomStatus} from '@app/features/user/state/CustomStatus';
import {makeAutoObservable, reaction} from 'mobx';

// kaynak-referans `@tebhos/constants/src/StatusConstants` yerine TEBHOS UserStatus dünyası.
const StatusTypes = {
	ONLINE: 'online',
	IDLE: 'idle',
	DND: 'dnd',
	INVISIBLE: 'invisible',
	OFFLINE: 'offline',
} as const satisfies Record<string, UserStatus>;

const VALID_STATUSES: ReadonlySet<UserStatus> = new Set<UserStatus>([
	'online',
	'idle',
	'dnd',
	'invisible',
	'offline',
]);

/** kaynak-referans `normalizeStatus` paritesi — geçersiz/bilinmeyen status'u "online"a düşürür. */
function normalizeStatus(status: string | null | undefined): UserStatus {
	return status != null && VALID_STATUSES.has(status as UserStatus) ? (status as UserStatus) : StatusTypes.ONLINE;
}

/**
 * Hesap-düzeyi presence niyeti (kaynak-referans `AccountStorage.AccountPresenceIntent`
 * yapısal karşılığı). Oturum değişimlerinde kullanıcının seçili status'unu ve
 * özel durumunu kısa süre taşımak için. AccountStorage TEBHOS'ta olmadığından
 * yalnızca tip olarak tanımlıdır; kalıcılaştırma katmanı bağlanınca kullanılır.
 */
export interface AccountPresenceIntent {
	status: UserStatus;
	statusResetsAt: string | null;
	statusResetsTo: UserStatus | null;
	customStatus: CustomStatus | null;
	capturedAt: number;
}

type Presence = Readonly<{
	status: UserStatus;
	since: number;
	afk: boolean;
	mobile: boolean;
	custom_status: GatewayCustomStatusPayload | null;
}>;

export const ACCOUNT_PRESENCE_INTENT_MAX_AGE_MS = 60 * 1000;

export interface LocalPresenceUserSettings {
	status: UserStatus;
	isHydrated(): boolean;
	markSessionChanging(): void;
	getAfkTimeout(): number;
	getCustomStatus(): CustomStatus | null;
	getStatusResetsAt(): string | null;
	getStatusResetsTo(): string | null;
}

let userSettings: LocalPresenceUserSettings | null = null;

export function setLocalPresenceUserSettings(settings: LocalPresenceUserSettings): void {
	userSettings = settings;
}

class LocalPresence {
	status: UserStatus = StatusTypes.ONLINE;
	since: number = 0;
	afk: boolean = false;
	mobile: boolean = false;
	customStatus: CustomStatus | null = null;
	private restoredIntent: AccountPresenceIntent | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		deferUntilModulesLoaded(() => {
			reaction(
				() => MobileLayout.isMobileLayout(),
				() => this.updatePresence(),
			);
		});
	}

	updatePresence(): void {
		const isMobile = MobileLayout.isMobileLayout();
		const settings = userSettings;
		if (!settings?.isHydrated()) {
			if (this.restoredIntent) {
				this.applyRestoredIntent(isMobile, settings);
				return;
			}
			this.status = StatusTypes.INVISIBLE;
			this.since = 0;
			this.afk = false;
			this.mobile = isMobile;
			this.customStatus = null;
			return;
		}
		this.restoredIntent = null;
		const userStatus = settings.status;
		const idleSince = Idle.getIdleSince();
		const afk = this.computeAfk(idleSince, isMobile, settings);
		const normalizedCustomStatus = normalizeCustomStatus(settings.getCustomStatus());
		this.customStatus = normalizedCustomStatus ? {...normalizedCustomStatus} : null;
		// DURUM-DÜZELTMESİ (2026-07-27): burada eskiden
		//   `userStatus === ONLINE && idleSince > 0 ? IDLE : userStatus`
		// vardı — kullanıcının SEÇTİĞİ status wire'a çıkmadan hemen önce
		// "Boşta"ya çevriliyordu. Ürün kararı: seçilen status her yerde
		// (kendi panelim + karşı tarafın üye listesi/DM/arkadaş listesi)
		// BİREBİR görünür. Boşta yalnızca kullanıcı bilerek seçerse gösterilir.
		// Hareketsizlik bilgisi kaybolmuyor: `afk` bayrağı + `since` hâlâ
		// hesaplanıp op3 ile gönderiliyor (bildirim/ses tarafı bunu kullanır).
		this.status = userStatus;
		this.since = idleSince;
		this.afk = afk;
		this.mobile = isMobile;
	}

	getStatus(): UserStatus {
		return this.status;
	}

	getPresence(): Presence {
		return {
			status: this.status,
			since: this.since,
			afk: this.afk,
			mobile: this.mobile,
			custom_status: toGatewayCustomStatus(this.customStatus),
		};
	}

	getGatewayPresence(): Presence | null {
		if (!userSettings?.isHydrated() && !this.restoredIntent) {
			return null;
		}
		return this.getPresence();
	}

	handleSessionChanging(options: {clearRestoredIntent?: boolean} = {}): void {
		if (options.clearRestoredIntent) {
			this.restoredIntent = null;
		}
		userSettings?.markSessionChanging();
		this.updatePresence();
	}

	captureIntent(): AccountPresenceIntent | null {
		const settings = userSettings;
		if (!settings?.isHydrated()) {
			return null;
		}
		const normalizedCustomStatus = normalizeCustomStatus(settings.getCustomStatus());
		return {
			status: settings.status,
			statusResetsAt: settings.getStatusResetsAt(),
			statusResetsTo: this.normalizeResetStatus(settings.getStatusResetsTo()),
			customStatus: normalizedCustomStatus ? {...normalizedCustomStatus} : null,
			capturedAt: Date.now(),
		};
	}

	restoreIntent(intent: AccountPresenceIntent | null | undefined): void {
		this.restoredIntent = intent && this.isIntentFresh(intent) ? this.normalizeIntent(intent) : null;
		this.updatePresence();
	}

	get presenceKey(): string {
		const hydrated = userSettings?.isHydrated() ? '1' : '0';
		const afk = this.afk ? '1' : '0';
		const mobile = this.mobile ? '1' : '0';
		return `hydrated:${hydrated}|${this.status}|${customStatusToKey(this.customStatus)}|afk:${afk}|mobile:${mobile}`;
	}

	private computeAfk(idleSince: number, isMobile: boolean, settings: LocalPresenceUserSettings | null): boolean {
		if (isMobile || idleSince <= 0) return false;
		const afkTimeout = settings?.getAfkTimeout() ?? 600;
		return Date.now() - idleSince > afkTimeout * 1000;
	}

	private applyRestoredIntent(isMobile: boolean, settings: LocalPresenceUserSettings | null): void {
		if (!this.restoredIntent) {
			return;
		}
		const idleSince = Idle.getIdleSince();
		// DURUM-DÜZELTMESİ (2026-07-27): updatePresence ile aynı kural — idle
		// override YOK, kullanıcının seçtiği status aynen taşınır.
		const userStatus = this.restoredIntent.status;
		this.customStatus = this.restoredIntent.customStatus ? {...this.restoredIntent.customStatus} : null;
		this.status = userStatus;
		this.since = idleSince;
		this.afk = this.computeAfk(idleSince, isMobile, settings);
		this.mobile = isMobile;
	}

	private normalizeIntent(intent: AccountPresenceIntent): AccountPresenceIntent {
		const statusResetsAt = intent.statusResetsAt ?? null;
		const statusResetsTo = this.normalizeResetStatus(intent.statusResetsTo);
		const expiresAt = statusResetsAt ? Date.parse(statusResetsAt) : null;
		const isExpired = expiresAt !== null && Number.isFinite(expiresAt) && expiresAt <= Date.now();
		const normalizedCustomStatus = normalizeCustomStatus(intent.customStatus);
		return {
			status: isExpired ? (statusResetsTo ?? StatusTypes.ONLINE) : normalizeStatus(intent.status),
			statusResetsAt: isExpired ? null : statusResetsAt,
			statusResetsTo: isExpired ? null : statusResetsTo,
			customStatus: normalizedCustomStatus ? {...normalizedCustomStatus} : null,
			capturedAt: intent.capturedAt,
		};
	}

	private isIntentFresh(intent: AccountPresenceIntent): boolean {
		const ageMs = Date.now() - intent.capturedAt;
		return Number.isFinite(intent.capturedAt) && ageMs >= 0 && ageMs <= ACCOUNT_PRESENCE_INTENT_MAX_AGE_MS;
	}

	private normalizeResetStatus(status: string | null | undefined): UserStatus | null {
		if (!status) {
			return null;
		}
		return normalizeStatus(status);
	}
}

export default new LocalPresence();
